import { FiltriLocali, TipoLocale, Provincia, TIPI_LOCALE_LABELS, PROVINCE_LABELS } from '../../types'

interface LocaleFiltersProps {
  filtri: FiltriLocali
  onFiltriChange: (filtri: FiltriLocali) => void
  totaleRisultati?: number
}

/**
 * Filtri per la lista locali
 * Include: provincia, tipo locale, fascia prezzo, ricerca testuale
 */
export default function LocaleFilters({
  filtri,
  onFiltriChange,
  totaleRisultati,
}: LocaleFiltersProps) {
  // Handler per aggiornare un singolo filtro
  const handleFilterChange = (key: keyof FiltriLocali, value: string) => {
    onFiltriChange({
      ...filtri,
      [key]: value || undefined, // undefined rimuove il filtro
    })
  }

  // Reset tutti i filtri
  const resetFiltri = () => {
    onFiltriChange({})
  }

  // Verifica se ci sono filtri attivi
  const hasFiltriAttivi = Object.values(filtri).some((v) => v !== undefined && v !== '')

  return (
    <div className="bg-night-light rounded-xl p-4 md:p-6 border border-gray-800">
      {/* Header filtri */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg text-white flex items-center gap-2">
          <svg className="w-5 h-5 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filtri
        </h3>
        {hasFiltriAttivi && (
          <button
            onClick={resetFiltri}
            className="text-sm text-gold hover:text-gold-light transition-colors"
          >
            Resetta filtri
          </button>
        )}
      </div>

      {/* Griglia filtri */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Ricerca testuale */}
        <div>
          <label htmlFor="ricerca" className="block text-sm text-gray-400 mb-1.5">
            Cerca
          </label>
          <div className="relative">
            <input
              id="ricerca"
              type="text"
              value={filtri.ricerca || ''}
              onChange={(e) => handleFilterChange('ricerca', e.target.value)}
              placeholder="Nome locale..."
              className="input pl-10"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Provincia */}
        <div>
          <label htmlFor="provincia" className="block text-sm text-gray-400 mb-1.5">
            Provincia
          </label>
          <select
            id="provincia"
            value={filtri.provincia || ''}
            onChange={(e) => handleFilterChange('provincia', e.target.value)}
            className="input"
          >
            <option value="">Tutte le province</option>
            {(Object.keys(PROVINCE_LABELS) as Provincia[]).map((prov) => (
              <option key={prov} value={prov}>
                {PROVINCE_LABELS[prov]}
              </option>
            ))}
          </select>
        </div>

        {/* Tipo locale */}
        <div>
          <label htmlFor="tipo" className="block text-sm text-gray-400 mb-1.5">
            Tipo locale
          </label>
          <select
            id="tipo"
            value={filtri.tipo || ''}
            onChange={(e) => handleFilterChange('tipo', e.target.value)}
            className="input"
          >
            <option value="">Tutti i tipi</option>
            {(Object.keys(TIPI_LOCALE_LABELS) as TipoLocale[]).map((tipo) => (
              <option key={tipo} value={tipo}>
                {TIPI_LOCALE_LABELS[tipo]}
              </option>
            ))}
          </select>
        </div>

        {/* Fascia prezzo */}
        <div>
          <label htmlFor="prezzo" className="block text-sm text-gray-400 mb-1.5">
            Fascia prezzo
          </label>
          <select
            id="prezzo"
            value={filtri.fasciaPrezzo || ''}
            onChange={(e) => handleFilterChange('fasciaPrezzo', e.target.value)}
            className="input"
          >
            <option value="">Tutte le fasce</option>
            <option value="€">€ - Economico</option>
            <option value="€€">€€ - Medio</option>
            <option value="€€€">€€€ - Alto</option>
          </select>
        </div>
      </div>

      {/* Risultati */}
      {totaleRisultati !== undefined && (
        <div className="mt-4 pt-4 border-t border-gray-800 flex items-center justify-between">
          <p className="text-sm text-gray-400">
            <span className="text-gold font-semibold">{totaleRisultati}</span>{' '}
            {totaleRisultati === 1 ? 'locale trovato' : 'locali trovati'}
          </p>
          {hasFiltriAttivi && (
            <div className="flex gap-2">
              {filtri.provincia && (
                <span className="text-xs bg-gold/20 text-gold px-2 py-1 rounded-full">
                  {PROVINCE_LABELS[filtri.provincia]}
                </span>
              )}
              {filtri.tipo && (
                <span className="text-xs bg-gold/20 text-gold px-2 py-1 rounded-full">
                  {TIPI_LOCALE_LABELS[filtri.tipo]}
                </span>
              )}
              {filtri.fasciaPrezzo && (
                <span className="text-xs bg-gold/20 text-gold px-2 py-1 rounded-full">
                  {filtri.fasciaPrezzo}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
